import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import { LuBot, LuMoreVertical, LuPencil, LuPlus, LuRefreshCw, LuSearch, LuSlidersHorizontal, LuSparkles, LuTrash2, LuWorkflow } from "react-icons/lu";

const SHARED_DARK = `
    --su-bg: #0c1317; --su-surface: #111b21; --su-raised: #202c33; --su-line: #2a3942; --su-line-2: #202c33; --su-control: #4e606b;
    --su-hover: #1a252c; --su-chip-on: #153543; --su-accent-ink: #5DCCED; --su-accent-hover: #0c8bb9;
    --su-ink: #e9edef; --su-ink-2: #aebac1; --su-ink-3: #9fb4b9; --su-ink-4: #7f929a;
    --su-wa-ink: #4cc96b; --su-red-ink: #ff7a8c; --su-pink-ink: #b3a8f0; --su-pink-bg: #2a2547;
    --su-scrim: rgba(0, 0, 0, 0.6); --su-shadow: 0 14px 36px -12px rgba(0, 0, 0, 0.7);
`;

export const BOT_LIST_CSS = `
.su-kit {
    --su-font: "Avenir LT Std", "Avenir", "Avenir Next", system-ui, sans-serif;
    --su-bg: #e6f4f9; --su-surface: #ffffff; --su-raised: #f1f6f8; --su-line: #e3e9ec; --su-line-2: #eef2f4; --su-control: #C3C3C3;
    --su-hover: #f1f7fa; --su-chip-on: #dff3fa; --su-accent: #0a7ba5; --su-accent-ink: #0a7ba5; --su-accent-hover: #08668a; --su-sky: #5DCCED;
    --su-ink: #2D2D2D; --su-ink-2: #555555; --su-ink-3: #747474; --su-ink-4: #8B8B8B;
    --su-wa: #2fae4e; --su-wa-ink: #1f8a3c; --su-red: #FF2242; --su-red-ink: #d6102f; --su-pink-ink: #5b49c0; --su-pink-bg: #ece9fa;
    --su-scrim: rgba(45, 45, 45, 0.45); --su-shadow: 0 14px 36px -12px rgba(45, 45, 45, 0.3);
    font-family: var(--su-font);
    color: var(--su-ink);
}
#body-dark.dark .su-kit { ${SHARED_DARK} }
.su-kit .su-ic { stroke-width: 1.8; flex-shrink: 0; }
.su-kit button { font-family: inherit; }
.su-kit button:focus-visible, .su-kit .su-card:focus-visible { outline: 2px solid var(--su-sky); outline-offset: 2px; }
.su-list-root { margin: 0 -1rem; width: calc(100% + 2rem); overflow: hidden; }
.su-head { flex-shrink: 0; background: var(--su-surface); border-bottom: 1px solid var(--su-line); }
.su-kit .heading { height: auto; min-height: 68px; margin: 0; padding: 12px 16px; gap: 14px; }
.su-head-right { display: flex; align-items: center; justify-content: flex-end; gap: 12px; flex: 1; min-width: 0; }
.su-kit .heading h1 { font-family: var(--su-font); font-size: 26px; font-weight: 600; color: var(--su-ink); margin: 0; white-space: nowrap; }
.su-count { font-size: 13px; color: var(--su-ink-3); white-space: nowrap; }
.su-filters { padding: 0 16px 14px; }
.su-search { display: flex; align-items: center; gap: 8px; flex: 0 1 320px; min-width: 180px; height: 42px; padding: 0 12px; background: var(--su-surface); border: 1px solid var(--su-line); border-radius: 11px; color: var(--su-ink-3); }
.su-search:focus-within { border-color: var(--su-sky); box-shadow: none; }
.su-kit .su-search input { flex: 1; min-width: 0; height: 100%; padding: 0; border: none !important; outline: none; box-shadow: none !important; background: transparent !important; color: var(--su-ink) !important; font-family: inherit; font-size: 14.5px; font-weight: 400; }
.su-kit .su-search input::placeholder { color: var(--su-ink-3); font-style: normal; }
.su-kit .su-search input:focus { outline: none; box-shadow: none !important; }
.su-btn { display: inline-flex; align-items: center; justify-content: center; gap: 7px; padding: 9px 15px; border: 1px solid transparent; border-radius: 9px; background: var(--su-accent); color: #ffffff; font-size: 13.5px; font-weight: 600; white-space: nowrap; cursor: pointer; }
.su-btn:hover { background: var(--su-accent-hover); }
.su-btn:disabled { background: var(--su-control); cursor: not-allowed; }
.su-btn.sm { padding: 6px 11px; font-size: 12.5px; }
.su-btn.ghost { background: var(--su-surface); border-color: var(--su-line); color: var(--su-ink-2); }
.su-btn.ghost:hover { background: var(--su-surface); border-color: var(--su-control); }
.su-btn.danger { background: var(--su-red); }
.su-btn.danger:hover { background: var(--su-red-ink); }
.su-link { padding: 2px 4px; border: none; background: none; color: var(--su-accent-ink); font-size: 13px; font-weight: 600; cursor: pointer; }
.su-chips { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.su-chips-sep { width: 1px; height: 18px; margin: 0 4px; background: var(--su-line); }
.su-chip { --c: var(--su-ink-3); display: inline-flex; align-items: center; gap: 6px; padding: 5px 11px; border: 1px solid var(--su-line); border-radius: 99px; background: var(--su-surface); color: var(--su-ink-2); font-size: 12.5px; font-weight: 600; white-space: nowrap; cursor: pointer; }
.su-chip:hover { border-color: var(--su-control); }
.su-chip i { display: block; width: 8px; height: 8px; border-radius: 3px; background: var(--c); }
.su-chip.on { border-color: var(--c); color: color-mix(in srgb, var(--c) 75%, var(--su-ink)); background: color-mix(in srgb, var(--c) 16%, var(--su-surface)); }
.su-board { flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; padding: 18px 16px; background: var(--su-bg); }
.su-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; }
@media (max-width: 1100px) { .su-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 640px) { .su-grid { grid-template-columns: minmax(0, 1fr); } }
.su-card { position: relative; display: flex; flex-direction: column; gap: 10px; min-width: 0; padding: 14px 12px 12px 16px; border: 1px solid var(--su-line); border-radius: 16px; background: var(--su-surface); cursor: pointer; }
.su-card:hover { border-color: var(--su-control); }
.su-card.live::before { content: ""; position: absolute; left: -1px; top: 14px; bottom: 14px; width: 4px; border-radius: 0 4px 4px 0; background: var(--su-wa); }
.su-card-h { display: flex; align-items: center; gap: 11px; min-width: 0; }
.su-card-name { flex: 1; min-width: 0; font-size: 16px; font-weight: 600; color: var(--su-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.su-av { --c: var(--su-accent); width: 42px; height: 42px; flex-shrink: 0; display: grid; place-items: center; border-radius: 50%; font-size: 15px; font-weight: 600; color: color-mix(in srgb, var(--c) 75%, var(--su-ink)); background: color-mix(in srgb, var(--c) 18%, var(--su-surface)); }
.su-ib { width: 30px; height: 30px; flex-shrink: 0; display: grid; place-items: center; border: none; border-radius: 8px; background: none; color: var(--su-ink-3); cursor: pointer; }
.su-ib:hover, .su-ib.on { background: var(--su-raised); }
.su-more { opacity: 0; }
.su-card:hover .su-more, .su-more:focus-visible, .su-more.on { opacity: 1; }
@media (hover: none) { .su-more { opacity: 1; } }
.su-card-d { margin: 0; min-height: 39px; font-size: 13.5px; line-height: 1.45; color: var(--su-ink-2); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.su-card-d.none { color: var(--su-ink-3); }
.su-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; font-size: 12.5px; color: var(--su-ink-3); }
.su-meta > span { display: inline-flex; align-items: center; gap: 5px; }
.su-tag { --c: var(--su-ink-3); padding: 2px 8px; border-radius: 99px; font-size: 12px; font-weight: 600; white-space: nowrap; color: color-mix(in srgb, var(--c) 72%, var(--su-ink)); background: color-mix(in srgb, var(--c) 16%, var(--su-surface)); }
.su-tag.dot::before { content: ""; width: 6px; height: 6px; border-radius: 2px; background: var(--c); }
.su-tag.ai { color: var(--su-pink-ink); background: var(--su-pink-bg); }
.su-card-f { display: flex; align-items: center; gap: 9px; padding-top: 10px; border-top: 1px solid var(--su-line-2); font-size: 13px; }
.su-state { font-weight: 600; color: var(--su-ink-3); }
.su-state.on { color: var(--su-wa-ink); }
.su-card-date { margin-left: auto; min-width: 0; font-size: 12px; color: var(--su-ink-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.su-sw { position: relative; width: 34px; height: 20px; flex-shrink: 0; padding: 0; border: none; border-radius: 99px; background: var(--su-control); cursor: pointer; }
.su-sw span { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #ffffff; transition: left 0.15s; }
.su-sw.on { background: var(--su-wa); }
.su-sw.on span { left: 16px; }
.su-pop { position: absolute; top: 44px; right: 10px; z-index: 50; min-width: 200px; padding: 6px; border: 1px solid var(--su-line); border-radius: 12px; background: var(--su-surface); box-shadow: var(--su-shadow); }
.su-pop-row { display: flex; align-items: center; gap: 10px; width: 100%; padding: 9px 10px; border: none; border-radius: 8px; background: none; color: var(--su-ink); font-size: 14px; text-align: left; cursor: pointer; }
.su-pop-row:hover { background: var(--su-hover); }
.su-pop-row .su-ic { color: var(--su-ink-3); }
.su-pop-row.danger, .su-pop-row.danger .su-ic { color: var(--su-red-ink); }
.su-pop-sep { height: 1px; margin: 4px 2px; background: var(--su-line-2); }
.su-pager { display: flex; align-items: center; gap: 4px; flex-shrink: 0; min-height: 50px; padding: 0 16px; border-top: 1px solid var(--su-line); background: var(--su-surface); font-size: 13px; color: var(--su-ink-3); }
.su-pager-nums { display: flex; gap: 4px; margin-left: auto; flex-wrap: wrap; }
.su-pager button { min-width: 30px; height: 30px; border: none; border-radius: 8px; background: none; color: var(--su-ink-3); font-size: 13px; font-weight: 600; cursor: pointer; }
.su-pager button:hover { background: var(--su-raised); }
.su-pager button.on { background: var(--su-accent); color: #ffffff; }
.su-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 80px 20px; text-align: center; color: var(--su-ink-4); }
.su-empty b { margin-top: 8px; font-size: 16px; font-weight: 600; color: var(--su-ink); }
.su-empty span { max-width: 44ch; font-size: 13.5px; line-height: 1.45; color: var(--su-ink-3); }
.su-empty-acts { display: flex; gap: 8px; margin-top: 10px; flex-wrap: wrap; justify-content: center; }
.su-scrim { position: fixed; inset: 0; z-index: 1300; display: flex; align-items: center; justify-content: center; padding: 24px; background: var(--su-scrim); }
.su-confirm { width: 430px; max-width: 100%; padding: 20px; border: 1px solid var(--su-line); border-radius: 16px; background: var(--su-surface); box-shadow: var(--su-shadow); }
.su-confirm h3 { margin: 0 0 8px; font-family: var(--su-font); font-size: 19px; font-weight: 600; line-height: 1.3; color: var(--su-ink); }
.su-confirm p { margin: 0; font-size: 14.5px; line-height: 1.5; color: var(--su-ink-2); }
.su-confirm-acts { display: flex; justify-content: flex-end; gap: 8px; margin-top: 18px; }

.su-kit .su-card-name, .su-kit .su-cb-name, .su-kit .su-nb-h h2, .su-kit .su-nb-opt b, .su-kit .su-cd-t b, .su-kit .su-acc-t b, .su-kit .su-ag-txt b, .su-kit .su-pal-h b, .su-kit .su-wa-id b, .su-kit .su-confirm h3, .su-kit .su-empty b { font-weight: 600; }
.su-kit .su-nb-sect, .su-kit .su-ag-cap, .su-kit .su-af-section > h4, .su-kit .su-pal-list h4, .su-kit .su-cf-sub { font-weight: 600; color: var(--su-ink-2); }
.su-kit .su-cf label, .su-kit .su-cf-label, .su-kit .su-af-label, .su-kit .su-nb-f label, .su-kit .su-nb-f > span { font-size: 12.5px; font-weight: 600; color: var(--su-ink-2); }
.su-kit .su-btn, .su-kit .su-chip, .su-kit .su-state, .su-kit .su-tag, .su-kit .su-ag-tabs button, .su-kit .su-cb-tabs button, .su-kit .su-cf-tabs button { font-weight: 600; }
.su-kit .su-pal-item, .su-kit .su-pop-row { font-weight: 600; }
.su-kit .heading h1 { font-weight: 600; }
.su-toasts .custom-toast-container { background: #2D2D2D !important; color: #FFFFFF !important; border-radius: 12px !important; font-size: 14px !important; font-weight: 600 !important; }
`;

export const useFullBleed = (ref) => {
    useLayoutEffect(() => {
        const update = () => {
            const element = ref.current;
            const parent = element?.parentElement;
            if (!parent) return;
            const box = parent.getBoundingClientRect();
            const nav = document.getElementById("content-nav");
            const start = nav ? nav.getBoundingClientRect().right : box.left;
            const end = document.documentElement.clientWidth;
            element.style.marginLeft = `${start - box.left}px`;
            element.style.marginRight = `${box.right - end}px`;
            element.style.width = `${end - start}px`;
        };
        update();
        const frame = window.requestAnimationFrame(update);
        window.addEventListener("resize", update);
        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener("resize", update);
        };
    }, [ref]);
};

export const TYPE_COLORS = {
    linear: { label: "Lineal", color: "#0a7ba5" },
    flows: { label: "Flujos", color: "#7c5cd6" },
    transactional: { label: "Transaccional", color: "#f26b1d" },
};

const AVATAR_COLORS = ["#0a7ba5", "#2fae4e", "#f26b1d", "#7c5cd6", "#14a3a3", "#F96480", "#e0a32e", "#FF2242"];

export const initials = (name) =>
    String(name || "?")
        .trim()
        .split(/\s+/)
        .filter((word) => /[\p{L}\p{N}]/u.test(word))
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase() || "?";

export const avatarColor = (name) => {
    const total = Array.from(String(name || "")).reduce((sum, char) => sum + char.codePointAt(0), 0);
    return AVATAR_COLORS[total % AVATAR_COLORS.length];
};

export const CardMenu = ({ items }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onMouseDown = (event) => {
            if (ref.current && !ref.current.contains(event.target)) setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    return (
        <div ref={ref} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
            <button
                type="button"
                className={`su-ib su-more${open ? " on" : ""}`}
                aria-label="Más acciones"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
            >
                <LuMoreVertical className="su-ic" size={17} />
            </button>
            {open && (
                <div className="su-pop" role="menu">
                    {items.map((item) => {
                        const Icon = item.icon;
                        return (
                            <React.Fragment key={item.label}>
                                {item.danger && <div className="su-pop-sep" />}
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={`su-pop-row${item.danger ? " danger" : ""}`}
                                    onClick={() => {
                                        setOpen(false);
                                        item.onClick();
                                    }}
                                >
                                    <Icon className="su-ic" size={16} />
                                    {item.label}
                                </button>
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const BotListCard = ({ bot, onEdit, onConfig, onClearStates, onDelete, onToggle }) => {
    const type = TYPE_COLORS[bot?.type] || TYPE_COLORS.linear;
    const description = (bot?.description || "").trim();
    const hasAgent = Boolean(bot?.agentConfig?.enabled);

    return (
        <article
            className={`su-card${bot.enabled ? " live" : ""}`}
            tabIndex={0}
            aria-label={`Editar ${bot?.name || "bot"}`}
            onClick={() => onEdit(bot)}
            onKeyDown={(event) => {
                if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    onEdit(bot);
                }
            }}
        >
            <div className="su-card-h">
                <span className="su-av" style={{ "--c": avatarColor(bot?.name) }} aria-hidden="true">
                    {initials(bot?.name)}
                </span>
                <span className="su-card-name" title={bot?.name}>
                    {bot?.name}
                </span>
                <CardMenu
                    items={[
                        { label: "Editar flujo", icon: LuPencil, onClick: () => onEdit(bot) },
                        { label: "Configuración", icon: LuSlidersHorizontal, onClick: () => onConfig(bot) },
                        { label: "Limpiar estados", icon: LuRefreshCw, onClick: () => onClearStates(bot) },
                        { label: "Eliminar bot", icon: LuTrash2, danger: true, onClick: () => onDelete(bot) },
                    ]}
                />
            </div>
            <p className={`su-card-d${description ? "" : " none"}`}>{description || "Sin descripción"}</p>
            <div className="su-meta">
                <span className="su-tag dot" style={{ "--c": type.color }}>
                    {type.label}
                </span>
                {typeof bot?.nodesCount === "number" && (
                    <span>
                        <LuWorkflow className="su-ic" size={14} />
                        {bot.nodesCount} nodo{bot.nodesCount === 1 ? "" : "s"}
                    </span>
                )}
                {hasAgent && (
                    <span className="su-tag ai">
                        <LuSparkles className="su-ic" size={12} />
                        Agente IA
                        {typeof bot?.agentToolsCount === "number" ? ` · ${bot.agentToolsCount} herramienta${bot.agentToolsCount === 1 ? "" : "s"}` : ""}
                    </span>
                )}
            </div>
            <div className="su-card-f">
                <button
                    type="button"
                    role="switch"
                    aria-checked={Boolean(bot.enabled)}
                    aria-label={`${bot.enabled ? "Desactivar" : "Activar"} ${bot?.name || "bot"}`}
                    className={`su-sw${bot.enabled ? " on" : ""}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        onToggle(bot);
                    }}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    <span />
                </button>
                <span className={`su-state${bot.enabled ? " on" : ""}`}>{bot.enabled ? "En uso" : "Inactivo"}</span>
                <span className="su-card-date">Creado {dayjs(bot.created).locale("es").format("DD MMM YYYY")}</span>
            </div>
        </article>
    );
};

export const ListPager = ({ total, page, byPage, setPage }) => {
    if (total === 0) return null;
    const pages = Math.max(1, Math.ceil(total / byPage));
    const current = Math.min(page, pages);
    const first = (current - 1) * byPage + 1;
    const last = Math.min(current * byPage, total);
    return (
        <div className="su-pager">
            <span>
                {first}–{last} de {total}
            </span>
            {pages > 1 && (
                <div className="su-pager-nums">
                    {Array.from({ length: pages }, (_, index) => index + 1).map((number) => (
                        <button
                            key={number}
                            type="button"
                            className={number === current ? "on" : ""}
                            aria-current={number === current ? "page" : undefined}
                            onClick={() => setPage(number)}
                        >
                            {number}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export const NoBots = ({ onCreate }) => (
    <div className="su-empty">
        <LuBot className="su-ic" size={44} />
        <b>Todavía no tienes bots</b>
        <span>Crea un agente con IA o arma un flujo nodo por nodo para atender tu WhatsApp.</span>
        <div className="su-empty-acts">
            <button type="button" className="su-btn" onClick={onCreate}>
                <LuPlus className="su-ic" size={16} />
                Crear mi primer bot
            </button>
        </div>
    </div>
);

export const NoResults = ({ search, onClear, onCreate }) => (
    <div className="su-empty">
        <LuSearch className="su-ic" size={40} />
        <b>{search ? `No hay bots con «${search}»` : "Ningún bot coincide con estos filtros"}</b>
        <span>{search ? "Revisa cómo está escrito o busca otro nombre." : "Quita algún filtro para ver más bots."}</span>
        <div className="su-empty-acts">
            <button type="button" className="su-btn ghost" onClick={onClear}>
                {search ? "Limpiar búsqueda" : "Limpiar filtros"}
            </button>
            <button type="button" className="su-btn" onClick={onCreate}>
                Crear bot
            </button>
        </div>
    </div>
);

export const ConfirmDialog = ({ confirm, onClose }) => {
    const cancelRef = useRef(null);

    useEffect(() => {
        if (!confirm) return undefined;
        cancelRef.current?.focus();
        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [confirm, onClose]);

    if (!confirm) return null;

    return createPortal(
        <div
            className="su-kit su-scrim"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <div className="su-confirm" role="alertdialog" aria-modal="true" aria-label={confirm.title}>
                <h3>{confirm.title}</h3>
                <p>{confirm.text}</p>
                <div className="su-confirm-acts">
                    <button ref={cancelRef} type="button" className="su-btn ghost" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="su-btn danger"
                        onClick={() => {
                            onClose();
                            confirm.run();
                        }}
                    >
                        {confirm.action}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};
